import { normalizeCompanyName } from './normalise.js';
import { contactRelevance } from './scoring.js';
import { activeCompanyTeamStatus } from './company-team-status.js';
import { routeRoleWithOpportunity } from './role-routing.js';
import { whyNowSuggestions } from './outreach-intelligence.js';
import { learnedRecommendationAdjustment } from './learning-recommender.js';
function preferredCategory(opportunity) {
    const text = [opportunity.type, opportunity.name, opportunity.recommendedAngle, ...(opportunity.serviceTags || [])].join(' ').toLowerCase();
    if (opportunity.type === 'new-partnership' || /partnership|sponsor|rights/.test(text))
        return 'Partnerships';
    if (/experiential|experience|activation|hospitality|fan|event|live/.test(text) || opportunity.type === 'future-event')
        return 'Events & Experiential';
    if (/creative|design|motion|3d|cgi|brand identity/.test(text))
        return 'Creative';
    if (/marketing|campaign|content|social|communications|comms/.test(text))
        return 'Marketing';
    return 'Misc';
}
function targetMatchesCompany(opportunity, companyName, companyId) {
    if (companyId && opportunity.companyId === companyId)
        return true;
    const key = normalizeCompanyName(companyName);
    if (!key)
        return false;
    if (normalizeCompanyName(opportunity.companyName || '') === key)
        return true;
    return (opportunity.targetCompanies || []).some(target => normalizeCompanyName(target.name) === key);
}
function recommendedTitleBonus(contact, opportunity) {
    const title = contact.title.toLowerCase();
    let bonus = 0;
    for (const recommended of opportunity.recommendedTitles || []) {
        const words = recommended.toLowerCase().split(/[^a-z0-9]+/).filter(word => word.length >= 4);
        const matches = words.filter(word => title.includes(word)).length;
        if (matches)
            bonus = Math.max(bonus, Math.min(12, matches * 4));
    }
    return bonus;
}
/**
 * Turns a Radar opportunity + target organisation into one explainable outreach
 * recommendation: team, person, why-now sentence and next action. It never
 * creates outreach or spends enrichment credits.
 */
export function opportunityOutreachPlan(opportunity, companyName, contacts, sequences, companies = []) {
    const whyNow = whyNowSuggestions(opportunity)[0];
    const companyKey = normalizeCompanyName(companyName);
    const company = companies.find(item => !item.archived && (item.id === opportunity.companyId || normalizeCompanyName(item.name) === companyKey));
    const companyContacts = contacts.filter(contact => !contact.archived && ((company?.id && contact.companyId === company.id) || normalizeCompanyName(contact.companyName) === companyKey));
    const activeIds = new Set(sequences.filter(sequence => !sequence.archived && sequence.stage !== 'sent').map(sequence => sequence.contactId));
    const sentIds = new Set(sequences.filter(sequence => !sequence.archived && sequence.stage === 'sent').map(sequence => sequence.contactId));
    const category = preferredCategory(opportunity);
    const activeStatus = activeCompanyTeamStatus(company, category);
    if (activeStatus) {
        const until = new Date(activeStatus.activeUntil);
        return { companyName, category, action: 'blocked-team', whyNow, angle: opportunity.recommendedAngle, reason: `${category} is currently suppressed until ${Number.isNaN(until.getTime()) ? activeStatus.activeUntil : until.toLocaleDateString('en-GB')}.`, confidence: 'high' };
    }
    const candidates = companyContacts
        .filter(contact => !contact.doNotContact && !contact.contactedAt && contact.deadLeadReason !== 'no-longer-at-company' && !sentIds.has(contact.id) && !activeIds.has(contact.id))
        .map(contact => {
        const routed = routeRoleWithOpportunity(contact.title, opportunity);
        let score = contactRelevance(contact.title);
        if (contact.crmCategory === category)
            score += 12;
        else if (routed.crmCategory === category)
            score += 7;
        score += recommendedTitleBonus(contact, opportunity);
        if (contact.emailVerification === 'verified')
            score += 5;
        const learned = learnedRecommendationAdjustment(contact, sequences, contacts, [opportunity]);
        score += learned.delta;
        return { contact, score, routed, learned };
    })
        .sort((a, b) => b.score - a.score || a.contact.fullName.localeCompare(b.contact.fullName));
    if (candidates.length) {
        const best = candidates[0];
        const bestCategory = best.contact.crmCategory === 'Misc' && best.routed.crmCategory !== 'Misc' ? best.routed.crmCategory : best.contact.crmCategory;
        const reasonParts = [best.contact.crmCategory === category ? 'Best untouched CRM contact in the opportunity team' : 'Best untouched relevant CRM contact'];
        if (best.learned.delta)
            reasonParts.push(`outcome learning ${best.learned.delta > 0 ? '+' : ''}${best.learned.delta}`);
        return { companyName, category: bestCategory, action: 'prepare-contact', contactId: best.contact.id, contactName: best.contact.fullName, contactTitle: best.contact.title, whyNow, angle: opportunity.recommendedAngle, reason: reasonParts.join(' · '), confidence: best.score >= 75 ? 'high' : best.score >= 60 ? 'medium' : 'low' };
    }
    const plannedSequence = sequences.find(sequence => {
        if (sequence.archived || sequence.stage === 'sent' || sequence.crmCategory !== category)
            return false;
        const contact = companyContacts.find(item => item.id === sequence.contactId);
        return !!contact;
    });
    const scheduled = plannedSequence ? companyContacts.find(contact => contact.id === plannedSequence.contactId) : undefined;
    if (scheduled)
        return { companyName, category, action: 'wait-scheduled', contactId: scheduled.id, contactName: scheduled.fullName, contactTitle: scheduled.title, whyNow, angle: opportunity.recommendedAngle, reason: `${category} outreach is already queued or scheduled for this organisation.`, confidence: 'high' };
    if (!targetMatchesCompany(opportunity, companyName, company?.id)) {
        return { companyName, category, action: 'no-action', whyNow, angle: opportunity.recommendedAngle, reason: 'This organisation is not currently linked to the opportunity target set.', confidence: 'low' };
    }
    return { companyName, category, action: 'find-people', whyNow, angle: opportunity.recommendedAngle, reason: companyContacts.length ? 'No eligible untouched CRM contact remains in the most relevant team.' : 'No CRM contacts are saved for this opportunity target yet.', confidence: 'medium' };
}
